import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import NotFound from "./NotFound";
import { Plus } from "lucide-react";
import { ShareButton } from "../components/ShareButton";
import { DietaryTags } from "../components/ProductCard";
import { EditEntityButton } from "../components/edit/EntityEditor";
import { toast } from "sonner";
import api from "../lib/api";
import { imgProps } from "../lib/img";
import { trackViewItem } from "../lib/analytics";
import { useCart } from "../context/CartContext";
import { usePageSeo, breadcrumbJsonLd } from "../lib/seo";
import { CANON, PICKUP_REASON } from "../data/nap";

const DELIVERY_METHODS = ["http://purl.org/goodrelations/v1#DeliveryModeOwnFleet", "http://purl.org/goodrelations/v1#DeliveryModePickUp"];
const dishJsonLd = (d) => ({
  "@context": "https://schema.org",
  "@type": "MenuItem",
  name: d.product.name,
  description: d.intro,
  image: d.product.image,
  url: `${CANON}/menu/${d.slug}`,
  ...(d.alternate_names?.length ? { alternateName: d.alternate_names } : {}),
  ...(d.suitable_for_diet ? { suitableForDiet: d.suitable_for_diet } : {}),
  offers: { "@type": "Offer", price: d.product.base_price.toFixed(2), priceCurrency: "USD", availability: d.product.in_stock === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
    availableDeliveryMethod: DELIVERY_METHODS, availableAtOrFrom: { "@type": "Restaurant", name: "Urban Turban by VeerGi", address: "2660 N Halsted St, Chicago, IL 60614" } },
});
const money = (v) => (v == null ? null : Number(v) % 1 ? `$${Number(v).toFixed(2)}` : `$${Number(v)}`);

function SoldOutBlock({ dish }) {
  return (
    <div className="mt-6 rounded-2xl border-2 border-neutral-300 bg-neutral-50 p-5" data-testid="dish-sold-out">
      <p className="font-display text-xl font-extrabold text-neutral-800">Sold out today</p>
      <p className="text-sm text-neutral-600 mt-1">The kitchen has run out of {dish.product.name} for now, so it can't be ordered. Nothing has been added to your cart.</p>
      {dish.alternatives?.length > 0 && (
        <div className="mt-4" data-testid="dish-alternatives">
          <p className="text-xs font-bold uppercase tracking-wide text-neutral-500">If you'd like, you could look at these instead — your choice, nothing is swapped for you</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            {dish.alternatives.map((a) => (
              <Link key={a.slug} to={`/menu/${a.slug}`} data-testid={`dish-alternative-${a.slug}`} className="bg-white border border-black/10 rounded-xl overflow-hidden hover-lift block">
                <img {...imgProps(a.image, [320, 480], "(min-width: 640px) 33vw, 100vw")} alt={a.name} width="320" height="96" className="w-full h-24 object-cover" loading="lazy" />
                <div className="p-2.5">
                  <p className="font-semibold text-sm leading-tight">{a.name}</p>
                  <p className="text-xs text-neutral-500 mt-0.5">${a.price.toFixed(2)} · see dish</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DishPage() {
  const { slug } = useParams();
  const [dish, setDish] = useState(null);
  const [missing, setMissing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [redirectTo, setRedirectTo] = useState(null);
  const { addFood, setDrawerOpen, deliveryInfo } = useCart();

  useEffect(() => {
    setDish(null); setMissing(false); setLoadError(false); setRedirectTo(null);
    api.get(`/dishes/${slug}`).then(({ data }) => { if (data.redirect) setRedirectTo(data.redirect); else { setDish(data); trackViewItem(data.product); } })
      .catch((err) => (err.response?.status === 404 ? setMissing(true) : setLoadError(true)));
    window.scrollTo(0, 0);
  }, [slug]);

  usePageSeo(dish ? {
    title: dish.seo_title,
    description: dish.meta_description,
    canonicalPath: `/menu/${slug}`,
    robots: "index, follow",
    ogImage: dish.product.image,
    jsonLd: [dishJsonLd(dish), breadcrumbJsonLd([["Home", "/"], [dish.business?.name || "Menu", `/store/${dish.store_slug}`], [dish.product.name, `/menu/${slug}`]]),
      ...(dish.faq?.length ? [{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: dish.faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) }] : [])],
  } : {});

  if (redirectTo) return <Navigate to={redirectTo} replace />;
  if (missing) return <Navigate to="/store/restaurant" replace />;
  if (loadError) return (
    <main className="max-w-5xl mx-auto px-4 py-16" data-testid="dish-load-error">
      <p className="text-neutral-700">We couldn't load this dish just now.</p>
      <button type="button" onClick={() => window.location.reload()} data-testid="dish-load-retry" className="mt-4 inline-flex rounded-full bg-black text-white text-sm font-bold px-5 h-10 items-center">Try again</button>
      <p className="text-sm text-neutral-500 mt-4"><Link to="/store/restaurant" className="underline">See the full Urban Turban menu</Link></p>
    </main>
  );
  if (!dish) return <main className="max-w-5xl mx-auto px-4 py-16"><p className="text-neutral-400">Loading…</p></main>;

  const soldOut = dish.product.in_stock === false;
  const addToCart = async () => {
    const ok = await addFood({ ...dish.product, display_price: dish.product.base_price, business_name: dish.business?.name });
    if (!ok) { api.get(`/dishes/${slug}`).then(({ data }) => { if (!data.redirect) setDish(data); }).catch(() => {}); return; }
    toast.success(`${dish.product.name} added to cart`);
    setDrawerOpen(true);
  };

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid={`dish-page-${slug}`}>
      <nav className="text-xs text-neutral-400 mb-4" aria-label="Breadcrumb" data-testid="dish-breadcrumb">
        <Link to="/" className="hover:text-black">Home</Link> / <Link to={`/store/${dish.store_slug}`} className="hover:text-black">{dish.business?.name}</Link> / <span className="text-neutral-600">{dish.product.name}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-8 items-start">
        <img {...imgProps(dish.product.image, [480, 640, 800], "(min-width: 768px) 40vw, 100vw")} alt={`${dish.product.name} at Urban Turban by VeerGi`} width="640" height="640" loading="eager" fetchPriority="high" className="w-full aspect-square object-cover rounded-3xl" />
        <div>
          <div className="flex items-start justify-between gap-3">
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight relative">{dish.h1 || dish.product.name}
              <EditEntityButton type="product" entity={dish.product} className="absolute -top-4 left-0" onSaved={(np) => setDish((d) => ({ ...d, product: { ...d.product, ...np } }))} />
            </h1>
            <ShareButton testId="share-dish" title={`${dish.product.name} — Urban Turban by VeerGi`} text={`${dish.product.name} at Urban Turban by VeerGi, Lincoln Park`} path={`/menu/${dish.product.slug || slug}`} className="shrink-0 mt-2" />
          </div>
          <div className="flex items-center gap-3 mt-3">
            <span className="font-bold text-xl">${dish.product.base_price.toFixed(2)}</span>
            {soldOut && <span className="text-xs font-bold uppercase tracking-wide bg-neutral-800 text-white px-2.5 py-1 rounded-full" data-testid="dish-sold-out-badge">Sold out today</span>}
            <span className="text-xs font-semibold text-neutral-400">{dish.business?.name}</span>
          </div>
          <DietaryTags tags={dish.product.dietary_tags} className="mt-3" />
          <p className="text-[15px] leading-relaxed text-neutral-700 mt-5">{dish.intro}</p>
          {dish.flavor && <p className="text-sm text-neutral-500 mt-3"><span className="font-bold text-neutral-700">Flavour:</span> {dish.flavor}</p>}
          {soldOut && <SoldOutBlock dish={dish} />}
          <div className="flex flex-wrap gap-3 mt-6">
            {!soldOut && (
            <button onClick={addToCart} data-testid="dish-add-to-cart"
              className="inline-flex items-center gap-2 bg-[#E04F33] hover:bg-[#C94228] text-white font-bold text-sm px-6 py-3 rounded-full transition-colors">
              <Plus size={16} /> Add to cart — ${dish.product.base_price.toFixed(2)}
            </button>
            )}
            <Link to={`/store/${dish.store_slug}`} data-testid="dish-view-menu"
              className="inline-flex items-center gap-2 border-2 border-neutral-200 hover:border-[#E04F33] font-bold text-sm px-6 py-3 rounded-full transition-colors">
              Full {dish.business?.name} menu
            </Link>
          </div>
          <p className="text-xs text-neutral-500 mt-2" data-testid="dish-pickup-reason">{PICKUP_REASON}</p>
          <div className="mt-6 text-sm text-neutral-500" data-testid="dish-availability">
            <p className="font-bold text-neutral-700 mb-1">Dine-in, pickup &amp; delivery</p>
            {dish.locations.map((l) => (
              <p key={l.id} className="mt-1" data-testid={`dish-location-${l.id}`}>{l.name} · {l.address} · <a href={`tel:${l.phone.replace(/\D/g, "")}`} className="text-[#E04F33] font-semibold">{l.phone}</a></p>
            ))}
            <p className="mt-1" data-testid="dish-delivery-line">
              Delivery direct from our kitchen{deliveryInfo?.customer_fee != null ? ` — ${money(deliveryInfo.customer_fee)}` : ""}{deliveryInfo?.free_delivery_over ? `, free over ${money(deliveryInfo.free_delivery_over)}` : ""}.
            </p>
            {dish.upcoming_locations?.map((l) => (
              <p key={l.id} className="mt-2 text-xs text-neutral-400" data-testid={`dish-upcoming-${l.id}`}>{l.name}: coming soon — not taking orders yet.</p>
            ))}
          </div>
        </div>
      </div>

      {dish.faq?.length > 0 && (
        <section className="mt-12" data-testid="dish-faq">
          <h2 className="font-display text-lg font-extrabold mb-4">Good to know</h2>
          <div className="space-y-3 max-w-3xl">
            {dish.faq.map(([q, a]) => (
              <details key={q} className="bg-white border border-black/5 rounded-2xl px-5 py-4">
                <summary className="font-semibold text-sm cursor-pointer">{q}</summary>
                <p className="text-sm text-neutral-600 mt-2">{a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {dish.related?.length > 0 && (
        <section className="mt-12" data-testid="dish-related">
          <h2 className="font-display text-lg font-extrabold mb-4">Goes well with</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-2xl">
            {dish.related.map((r) => (
              <Link key={r.slug} to={`/menu/${r.slug}`} data-testid={`dish-related-${r.slug}`} className="bg-white border border-black/5 rounded-2xl overflow-hidden hover-lift block">
                <img {...imgProps(r.image, [320, 480], "(min-width: 640px) 33vw, 50vw")} alt={r.name} width="320" height="112" className="w-full h-28 object-cover" loading="lazy" />
                <div className="p-3">
                  <p className="font-semibold text-sm leading-tight">{r.name}</p>
                  <p className="text-xs text-neutral-500 mt-1">${r.price.toFixed(2)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
